import { useState } from 'react';

interface Template {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  pages: number;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  tags: string[];
}

interface TemplateSelectorProps {
  onSelectTemplate: (template: Template) => void;
}

interface BookPreviewModalProps {
  template: Template;
  onClose: () => void;
  onExport: () => void;
}

const BookPreviewModal: React.FC<BookPreviewModalProps> = ({ template, onClose, onExport }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [showCover, setShowCover] = useState(true);

  const samplePages = [
    {
      title: "Chapter 1: Introduction",
      content: "This is where your compelling introduction begins. The opening chapter sets the tone for your entire book and draws readers into your narrative. Use vivid descriptions and engaging storytelling to capture their attention from the very first page."
    },
    {
      title: "Chapter 2: Main Content",
      content: "The main body of your book contains the core message and valuable insights. Each chapter builds upon the previous one, creating a cohesive narrative that guides readers through your ideas. Include examples, case studies, and practical applications."
    },
    {
      title: "Chapter 3: Advanced Topics",
      content: "As readers progress through your book, introduce more advanced concepts and deeper insights. This section demonstrates your expertise and provides readers with actionable knowledge they can apply immediately."
    },
    {
      title: "Chapter 4: Conclusion",
      content: "The conclusion ties everything together, reinforcing key points and leaving readers with a clear understanding of your message. End with a call to action that inspires readers to take the next step."
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Book Preview</h2>
            <p className="text-sm text-gray-600 mt-1">{template.title} Template</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onExport}
              className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-download-line"></i>
              Export Book
            </button>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* View Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => setShowCover(true)}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                showCover
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <i className="ri-book-line mr-2"></i>
              Cover View
            </button>
            <button
              onClick={() => setShowCover(false)}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                !showCover
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <i className="ri-pages-line mr-2"></i>
              Pages View
            </button>
          </div>

          {showCover ? (
            /* Cover View */
            <div className="flex items-center justify-center">
              <div className="relative" style={{ perspective: '2000px' }}>
                {/* Book Shadow */}
                <div className="absolute inset-0 bg-black/20 blur-3xl transform translate-y-8 scale-95"></div>
                
                {/* Book Cover */}
                <div 
                  className="relative bg-white rounded-r-lg shadow-2xl"
                  style={{
                    width: '600px',
                    height: '800px',
                    transformStyle: 'preserve-3d',
                    transform: 'rotateY(-15deg) rotateX(5deg)'
                  }}
                >
                  {/* Cover Image */}
                  <div className="absolute inset-0 rounded-r-lg overflow-hidden">
                    <img
                      src={template.image}
                      alt={template.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
                  </div>

                  {/* Cover Content */}
                  <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <div>
                      <div className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
                        {template.category}
                      </div>
                      <h1 className="text-5xl font-bold mb-4 leading-tight drop-shadow-lg">
                        {template.title}
                      </h1>
                      <p className="text-xl opacity-90 drop-shadow-md">
                        {template.description}
                      </p>
                    </div>
                    <div>
                      <p className="text-lg font-medium">Author Name</p>
                      <div className="flex items-center gap-2 mt-2 text-sm opacity-80">
                        <i className="ri-pages-line"></i>
                        <span>{template.estimatedPages} Pages</span>
                        <span className="mx-2">•</span>
                        <i className="ri-time-line"></i>
                        <span>{template.estimatedTime}</span>
                      </div>
                    </div>
                  </div>

                  {/* Book Spine */}
                  <div 
                    className="absolute left-0 top-0 h-full bg-gradient-to-r from-gray-800 to-gray-700"
                    style={{
                      width: '50px',
                      transformOrigin: 'left',
                      transform: 'rotateY(-90deg)',
                      boxShadow: 'inset -10px 0 20px rgba(0,0,0,0.3)'
                    }}
                  >
                    <div className="h-full flex items-center justify-center">
                      <p className="text-white text-sm font-bold transform -rotate-90 whitespace-nowrap">
                        {template.title}
                      </p>
                    </div>
                  </div>

                  {/* Book Pages Edge */}
                  <div 
                    className="absolute right-0 top-0 h-full"
                    style={{
                      width: '20px',
                      background: 'repeating-linear-gradient(to bottom, #f9fafb 0px, #f9fafb 2px, #e5e7eb 2px, #e5e7eb 4px)',
                      boxShadow: 'inset 5px 0 10px rgba(0,0,0,0.1)'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ) : (
            /* Pages View */
            <div className="max-w-5xl mx-auto">
              {/* Page Navigation */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="px-4 py-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Previous
                </button>
                <span className="text-sm font-medium text-gray-600">
                  Page {currentPage + 1} of {samplePages.length}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(samplePages.length - 1, currentPage + 1))}
                  disabled={currentPage === samplePages.length - 1}
                  className="px-4 py-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  Next
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>

              {/* Book Pages Spread */}
              <div className="relative bg-white rounded-lg shadow-2xl p-12" style={{ minHeight: '700px' }}>
                {/* Page Content */}
                <div className="prose prose-lg max-w-none">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    {samplePages[currentPage].title}
                  </h2>
                  <div className="text-gray-700 leading-relaxed space-y-4">
                    {samplePages[currentPage].content.split('. ').map((sentence, idx) => (
                      <p key={idx} className="text-justify">
                        {sentence}{sentence.endsWith('.') ? '' : '.'}
                      </p>
                    ))}
                  </div>
                </div>

                {/* Page Number */}
                <div className="absolute bottom-6 right-12 text-sm text-gray-400 font-medium">
                  {currentPage + 1}
                </div>

                {/* Page Fold Effect */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-transparent to-gray-100 opacity-30"></div>
              </div>

              {/* Page Thumbnails */}
              <div className="flex items-center justify-center gap-3 mt-6">
                {samplePages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPage(idx)}
                    className={`w-12 h-16 rounded border-2 transition-all ${
                      currentPage === idx
                        ? 'border-purple-600 bg-purple-50 shadow-lg scale-110'
                        : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                    }`}
                  >
                    <div className="text-xs text-gray-500 font-medium">{idx + 1}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({ onSelectTemplate }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const categories = [
    'All', 'Business', 'Fiction', 'Self-Help', 'Educational', 'Health', 
    'Technology', 'Marketing', 'Finance', 'Lifestyle', 'Travel', 'Cooking', 'Biography'
  ];

  const templates: Template[] = [
    // Business Templates (8)
    {
      id: 'bus-001',
      title: 'Leadership Excellence Guide',
      category: 'Business',
      description: 'Comprehensive guide to developing leadership skills and managing teams effectively in modern organizations.',
      image: 'https://public.readdy.ai/ai/img_res/9b7a6742e97267085282b064e96909e6.jpg',
      pages: 120,
      difficulty: 'Intermediate',
      tags: ['leadership', 'management', 'teams', 'corporate']
    },
    {
      id: 'bus-002',
      title: 'Digital Marketing Mastery',
      category: 'Business',
      description: 'Complete blueprint for building successful digital marketing campaigns across all major platforms.',
      image: 'https://readdy.ai/api/search-image?query=Digital%20marketing%20book%20cover%20titled%20Digital%20Marketing%20Mastery%20with%20social%20media%20icons%2C%20analytics%20charts%2C%20modern%20tech%20design%2C%20vibrant%20colors%2C%20professional%20marketing%20book%20layout&width=400&height=300&seq=bus002&orientation=landscape',
      pages: 150,
      difficulty: 'Advanced',
      tags: ['marketing', 'digital', 'social media', 'analytics']
    },
    {
      id: 'bus-003',
      title: 'Startup Success Blueprint',
      category: 'Business',
      description: 'Step-by-step guide to launching and scaling a successful startup from idea to IPO.',
      image: 'https://readdy.ai/api/search-image?query=Startup%20business%20book%20cover%20titled%20Startup%20Success%20Blueprint%20with%20rocket%20launch%2C%20entrepreneur%20silhouette%2C%20modern%20startup%20design%2C%20orange%20and%20blue%20colors%2C%20innovation%20theme&width=400&height=300&seq=bus003&orientation=landscape',
      pages: 180,
      difficulty: 'Advanced',
      tags: ['startup', 'entrepreneurship', 'business plan', 'scaling']
    },
    {
      id: 'bus-004',
      title: 'Sales Conversion Secrets',
      category: 'Business',
      description: 'Proven strategies and techniques to increase sales conversion rates and close more deals.',
      image: 'https://readdy.ai/api/search-image?query=Sales%20book%20cover%20titled%20Sales%20Conversion%20Secrets%20with%20handshake%2C%20growth%20arrow%2C%20professional%20sales%20design%2C%20green%20and%20black%20colors%2C%20business%20success%20theme&width=400&height=300&seq=bus004&orientation=landscape',
      pages: 100,
      difficulty: 'Intermediate',
      tags: ['sales', 'conversion', 'negotiation', 'closing']
    },
    {
      id: 'bus-005',
      title: 'Remote Team Management',
      category: 'Business',
      description: 'Essential guide for managing distributed teams and maintaining productivity in remote work environments.',
      image: 'https://readdy.ai/api/search-image?query=Remote%20work%20book%20cover%20titled%20Remote%20Team%20Management%20with%20home%20office%20setup%2C%20video%20conference%20icons%2C%20modern%20remote%20work%20design%2C%20purple%20and%20white%20colors&width=400&height=300&seq=bus005&orientation=landscape',
      pages: 90,
      difficulty: 'Beginner',
      tags: ['remote work', 'team management', 'productivity', 'communication']
    },
    {
      id: 'bus-006',
      title: 'Financial Planning for Business',
      category: 'Business',
      description: 'Comprehensive guide to business financial planning, budgeting, and investment strategies.',
      image: 'https://readdy.ai/api/search-image?query=Business%20finance%20book%20cover%20titled%20Financial%20Planning%20for%20Business%20with%20calculator%2C%20charts%2C%20money%20symbols%2C%20professional%20financial%20design%2C%20navy%20blue%20and%20gold%20colors&width=400&height=300&seq=bus006&orientation=landscape',
      pages: 130,
      difficulty: 'Intermediate',
      tags: ['finance', 'planning', 'budgeting', 'investment']
    },
    {
      id: 'bus-007',
      title: 'Customer Service Excellence',
      category: 'Business',
      description: 'Best practices for delivering exceptional customer service and building lasting customer relationships.',
      image: 'https://readdy.ai/api/search-image?query=Customer%20service%20book%20cover%20titled%20Customer%20Service%20Excellence%20with%20smiling%20customer%20service%20representative%2C%20five%20stars%2C%20professional%20service%20design%2C%20teal%20and%20white%20colors&width=400&height=300&seq=bus007&orientation=landscape',
      pages: 85,
      difficulty: 'Beginner',
      tags: ['customer service', 'support', 'relationships', 'satisfaction']
    },
    {
      id: 'bus-008',
      title: 'Project Management Pro',
      category: 'Business',
      description: 'Advanced project management methodologies and tools for delivering successful projects on time and budget.',
      image: 'https://readdy.ai/api/search-image?query=Project%20management%20book%20cover%20titled%20Project%20Management%20Pro%20with%20gantt%20chart%2C%20timeline%2C%20project%20icons%2C%20professional%20PM%20design%2C%20red%20and%20gray%20colors&width=400&height=300&seq=bus008&orientation=landscape',
      pages: 140,
      difficulty: 'Advanced',
      tags: ['project management', 'planning', 'methodology', 'tools']
    },

    // Fiction Templates (8)
    {
      id: 'fic-001',
      title: 'Mystery Novel Template',
      category: 'Fiction',
      description: 'Classic mystery novel structure with detective protagonist, clues, red herrings, and satisfying resolution.',
      image: 'https://readdy.ai/api/search-image?query=Mystery%20novel%20book%20cover%20with%20dark%20atmospheric%20design%2C%20detective%20silhouette%2C%20magnifying%20glass%2C%20foggy%20street%20scene%2C%20noir%20style%20typography%2C%20dark%20blue%20and%20black%20colors&width=400&height=300&seq=fic001&orientation=landscape',
      pages: 250,
      difficulty: 'Intermediate',
      tags: ['mystery', 'detective', 'crime', 'suspense']
    },
    {
      id: 'fic-002',
      title: 'Romance Story Framework',
      category: 'Fiction',
      description: 'Heartwarming romance template with character development, relationship building, and emotional journey.',
      image: 'https://readdy.ai/api/search-image?query=Romance%20novel%20book%20cover%20with%20elegant%20couple%20silhouette%2C%20heart%20motifs%2C%20romantic%20sunset%2C%20flowing%20script%20typography%2C%20pink%20and%20gold%20colors%2C%20love%20story%20design&width=400&height=300&seq=fic002&orientation=landscape',
      pages: 200,
      difficulty: 'Beginner',
      tags: ['romance', 'love', 'relationships', 'emotion']
    },
    {
      id: 'fic-003',
      title: 'Sci-Fi Adventure Epic',
      category: 'Fiction',
      description: 'Futuristic science fiction template with advanced technology, space exploration, and alien encounters.',
      image: 'https://readdy.ai/api/search-image?query=Science%20fiction%20book%20cover%20with%20futuristic%20spaceship%2C%20alien%20planet%2C%20cosmic%20background%2C%20metallic%20typography%2C%20blue%20and%20silver%20colors%2C%20space%20adventure%20theme&width=400&height=300&seq=fic003&orientation=landscape',
      pages: 300,
      difficulty: 'Advanced',
      tags: ['sci-fi', 'space', 'technology', 'adventure']
    },
    {
      id: 'fic-004',
      title: 'Fantasy Quest Template',
      category: 'Fiction',
      description: 'Epic fantasy adventure with magical worlds, mythical creatures, and heroic quests.',
      image: 'https://readdy.ai/api/search-image?query=Fantasy%20novel%20book%20cover%20with%20magical%20castle%2C%20dragon%20silhouette%2C%20enchanted%20forest%2C%20mystical%20symbols%2C%20ornate%20typography%2C%20purple%20and%20gold%20colors&width=400&height=300&seq=fic004&orientation=landscape',
      pages: 280,
      difficulty: 'Advanced',
      tags: ['fantasy', 'magic', 'quest', 'adventure']
    },
    {
      id: 'fic-005',
      title: 'Thriller Suspense Plot',
      category: 'Fiction',
      description: 'High-stakes thriller template with psychological suspense, plot twists, and edge-of-seat tension.',
      image: 'https://readdy.ai/api/search-image?query=Thriller%20book%20cover%20with%20dramatic%20shadows%2C%20running%20figure%2C%20city%20skyline%20at%20night%2C%20bold%20typography%2C%20black%20and%20red%20colors%2C%20suspense%20atmosphere&width=400&height=300&seq=fic005&orientation=landscape',
      pages: 220,
      difficulty: 'Intermediate',
      tags: ['thriller', 'suspense', 'psychological', 'tension']
    },
    {
      id: 'fic-006',
      title: 'Historical Fiction Guide',
      category: 'Fiction',
      description: 'Period piece template set in historical eras with authentic details and compelling characters.',
      image: 'https://readdy.ai/api/search-image?query=Historical%20fiction%20book%20cover%20with%20vintage%20photograph%2C%20period%20costume%20silhouette%2C%20old%20manuscript%20background%2C%20classic%20typography%2C%20sepia%20and%20brown%20colors&width=400&height=300&seq=fic006&orientation=landscape',
      pages: 240,
      difficulty: 'Intermediate',
      tags: ['historical', 'period', 'authentic', 'drama']
    },
    {
      id: 'fic-007',
      title: 'Young Adult Coming of Age',
      category: 'Fiction',
      description: 'Teen-focused story template exploring identity, friendship, and personal growth during adolescence.',
      image: 'https://readdy.ai/api/search-image?query=Young%20adult%20book%20cover%20with%20teenage%20silhouette%2C%20school%20hallway%2C%20friendship%20symbols%2C%20modern%20typography%2C%20bright%20colors%2C%20coming%20of%20age%20theme&width=400&height=300&seq=fic007&orientation=landscape',
      pages: 180,
      difficulty: 'Beginner',
      tags: ['young adult', 'teen', 'growth', 'identity']
    },
    {
      id: 'fic-008',
      title: 'Literary Fiction Template',
      category: 'Fiction',
      description: 'Character-driven literary work focusing on human condition, relationships, and philosophical themes.',
      image: 'https://readdy.ai/api/search-image?query=Literary%20fiction%20book%20cover%20with%20artistic%20abstract%20design%2C%20thoughtful%20portrait%2C%20philosophical%20symbols%2C%20elegant%20typography%2C%20muted%20earth%20tones&width=400&height=300&seq=fic008&orientation=landscape',
      pages: 260,
      difficulty: 'Advanced',
      tags: ['literary', 'character-driven', 'philosophical', 'artistic']
    },

    // Self-Help Templates (8)
    {
      id: 'self-001',
      title: 'Personal Development Mastery',
      category: 'Self-Help',
      description: 'Complete guide to personal growth, goal setting, and achieving your full potential in life.',
      image: 'https://readdy.ai/api/search-image?query=Self-help%20book%20cover%20titled%20Personal%20Development%20Mastery%20with%20person%20climbing%20mountain%20peak%2C%20success%20symbols%2C%20motivational%20design%2C%20orange%20and%20blue%20colors&width=400&height=300&seq=self001&orientation=landscape',
      pages: 160,
      difficulty: 'Intermediate',
      tags: ['personal growth', 'goals', 'potential', 'success']
    },
    {
      id: 'self-002',
      title: 'Productivity Optimization',
      category: 'Self-Help',
      description: 'Proven strategies to maximize productivity, manage time effectively, and achieve more with less effort.',
      image: 'https://readdy.ai/api/search-image?query=Productivity%20book%20cover%20titled%20Productivity%20Optimization%20with%20clock%2C%20checklist%2C%20efficiency%20symbols%2C%20clean%20modern%20design%2C%20green%20and%20white%20colors&width=400&height=300&seq=self002&orientation=landscape',
      pages: 120,
      difficulty: 'Beginner',
      tags: ['productivity', 'time management', 'efficiency', 'organization']
    },
    {
      id: 'self-003',
      title: 'Confidence Building Blueprint',
      category: 'Self-Help',
      description: 'Step-by-step approach to building unshakeable confidence and overcoming self-doubt.',
      image: 'https://readdy.ai/api/search-image?query=Confidence%20book%20cover%20titled%20Confidence%20Building%20Blueprint%20with%20confident%20person%20pose%2C%20strength%20symbols%2C%20empowerment%20design%2C%20bold%20red%20and%20black%20colors&width=400&height=300&seq=self003&orientation=landscape',
      pages: 140,
      difficulty: 'Intermediate',
      tags: ['confidence', 'self-esteem', 'empowerment', 'mindset']
    },
    {
      id: 'self-004',
      title: 'Stress Management Guide',
      category: 'Self-Help',
      description: 'Comprehensive strategies for managing stress, anxiety, and maintaining mental well-being.',
      image: 'https://readdy.ai/api/search-image?query=Stress%20management%20book%20cover%20titled%20Stress%20Management%20Guide%20with%20peaceful%20meditation%20pose%2C%20zen%20symbols%2C%20calming%20design%2C%20soft%20blue%20and%20green%20colors&width=400&height=300&seq=self004&orientation=landscape',
      pages: 110,
      difficulty: 'Beginner',
      tags: ['stress management', 'anxiety', 'wellness', 'relaxation']
    },
    {
      id: 'self-005',
      title: 'Habit Formation System',
      category: 'Self-Help',
      description: 'Science-based approach to building positive habits and breaking negative patterns permanently.',
      image: 'https://readdy.ai/api/search-image?query=Habit%20formation%20book%20cover%20titled%20Habit%20Formation%20System%20with%20calendar%20checkmarks%2C%20progress%20chart%2C%20routine%20symbols%2C%20systematic%20design%2C%20purple%20and%20yellow%20colors&width=400&height=300&seq=self005&orientation=landscape',
      pages: 130,
      difficulty: 'Intermediate',
      tags: ['habits', 'behavior change', 'routine', 'consistency']
    },
    {
      id: 'self-006',
      title: 'Mindfulness and Meditation',
      category: 'Self-Help',
      description: 'Practical guide to mindfulness practices, meditation techniques, and present-moment awareness.',
      image: 'https://readdy.ai/api/search-image?query=Mindfulness%20book%20cover%20titled%20Mindfulness%20and%20Meditation%20with%20lotus%20flower%2C%20meditation%20pose%2C%20zen%20garden%2C%20peaceful%20design%2C%20soft%20purple%20and%20white%20colors&width=400&height=300&seq=self006&orientation=landscape',
      pages: 100,
      difficulty: 'Beginner',
      tags: ['mindfulness', 'meditation', 'awareness', 'peace']
    },
    {
      id: 'self-007',
      title: 'Emotional Intelligence Mastery',
      category: 'Self-Help',
      description: 'Develop emotional intelligence skills for better relationships and personal success.',
      image: 'https://readdy.ai/api/search-image?query=Emotional%20intelligence%20book%20cover%20titled%20Emotional%20Intelligence%20Mastery%20with%20heart%20and%20brain%20symbols%2C%20connection%20icons%2C%20professional%20design%2C%20teal%20and%20orange%20colors&width=400&height=300&seq=self007&orientation=landscape',
      pages: 150,
      difficulty: 'Advanced',
      tags: ['emotional intelligence', 'relationships', 'empathy', 'communication']
    },
    {
      id: 'self-008',
      title: 'Life Purpose Discovery',
      category: 'Self-Help',
      description: 'Journey to discovering your life purpose, passion, and creating meaningful existence.',
      image: 'https://readdy.ai/api/search-image?query=Life%20purpose%20book%20cover%20titled%20Life%20Purpose%20Discovery%20with%20compass%2C%20path%20through%20forest%2C%20journey%20symbols%2C%20inspirational%20design%2C%20gold%20and%20deep%20blue%20colors&width=400&height=300&seq=self008&orientation=landscape',
      pages: 170,
      difficulty: 'Advanced',
      tags: ['purpose', 'passion', 'meaning', 'fulfillment']
    },

    // Educational Templates (8)
    {
      id: 'edu-001',
      title: 'Online Course Creation',
      category: 'Educational',
      description: 'Complete guide to creating, marketing, and selling successful online courses.',
      image: 'https://readdy.ai/api/search-image?query=Online%20education%20book%20cover%20titled%20Online%20Course%20Creation%20with%20laptop%20screen%2C%20graduation%20cap%2C%20e-learning%20symbols%2C%20educational%20design%2C%20blue%20and%20orange%20colors&width=400&height=300&seq=edu001&orientation=landscape',
      pages: 180,
      difficulty: 'Intermediate',
      tags: ['online courses', 'education', 'teaching', 'e-learning']
    },
    {
      id: 'edu-002',
      title: 'Study Skills Mastery',
      category: 'Educational',
      description: 'Effective study techniques, memory improvement, and academic success strategies.',
      image: 'https://readdy.ai/api/search-image?query=Study%20skills%20book%20cover%20titled%20Study%20Skills%20Mastery%20with%20open%20books%2C%20brain%20with%20gears%2C%20academic%20symbols%2C%20scholarly%20design%2C%20navy%20blue%20and%20yellow%20colors&width=400&height=300&seq=edu002&orientation=landscape',
      pages: 120,
      difficulty: 'Beginner',
      tags: ['study skills', 'memory', 'learning', 'academic']
    },
    {
      id: 'edu-003',
      title: 'Research Methodology Guide',
      category: 'Educational',
      description: 'Comprehensive approach to conducting academic research and writing scholarly papers.',
      image: 'https://readdy.ai/api/search-image?query=Research%20methodology%20book%20cover%20titled%20Research%20Methodology%20Guide%20with%20microscope%2C%20data%20charts%2C%20academic%20papers%2C%20scholarly%20design%2C%20gray%20and%20red%20colors&width=400&height=300&seq=edu003&orientation=landscape',
      pages: 200,
      difficulty: 'Advanced',
      tags: ['research', 'methodology', 'academic writing', 'analysis']
    },
    {
      id: 'edu-004',
      title: 'Language Learning System',
      category: 'Educational',
      description: 'Effective methods for learning new languages quickly and retaining vocabulary long-term.',
      image: 'https://readdy.ai/api/search-image?query=Language%20learning%20book%20cover%20titled%20Language%20Learning%20System%20with%20speech%20bubbles%20in%20different%20languages%2C%20globe%2C%20communication%20symbols%2C%20multilingual%20design%2C%20green%20and%20blue%20colors&width=400&height=300&seq=edu004&orientation=landscape',
      pages: 150,
      difficulty: 'Intermediate',
      tags: ['language learning', 'vocabulary', 'communication', 'multilingual']
    },
    {
      id: 'edu-005',
      title: 'STEM Education Guide',
      category: 'Educational',
      description: 'Engaging approaches to teaching and learning science, technology, engineering, and mathematics.',
      image: 'https://readdy.ai/api/search-image?query=STEM%20education%20book%20cover%20titled%20STEM%20Education%20Guide%20with%20science%20beakers%2C%20mathematical%20formulas%2C%20engineering%20tools%2C%20technical%20design%2C%20bright%20colors&width=400&height=300&seq=edu005&orientation=landscape',
      pages: 160,
      difficulty: 'Advanced',
      tags: ['STEM', 'science', 'technology', 'engineering', 'mathematics']
    },
    {
      id: 'edu-006',
      title: 'Critical Thinking Development',
      category: 'Educational',
      description: 'Developing analytical thinking skills, logical reasoning, and problem-solving abilities.',
      image: 'https://readdy.ai/api/search-image?query=Critical%20thinking%20book%20cover%20titled%20Critical%20Thinking%20Development%20with%20puzzle%20pieces%2C%20lightbulb%2C%20analytical%20symbols%2C%20intellectual%20design%2C%20purple%20and%20orange%20colors&width=400&height=300&seq=edu006&orientation=landscape',
      pages: 140,
      difficulty: 'Intermediate',
      tags: ['critical thinking', 'analysis', 'reasoning', 'problem-solving']
    },
    {
      id: 'edu-007',
      title: 'Educational Technology Integration',
      category: 'Educational',
      description: 'Leveraging technology tools and platforms to enhance learning and teaching effectiveness.',
      image: 'https://readdy.ai/api/search-image?query=Educational%20technology%20book%20cover%20titled%20Educational%20Technology%20Integration%20with%20tablet%2C%20interactive%20whiteboard%2C%20tech%20tools%2C%20modern%20education%20design%2C%20cyan%20and%20black%20colors&width=400&height=300&seq=edu007&orientation=landscape',
      pages: 130,
      difficulty: 'Intermediate',
      tags: ['educational technology', 'digital learning', 'teaching tools', 'innovation']
    },
    {
      id: 'edu-008',
      title: 'Adult Learning Strategies',
      category: 'Educational',
      description: 'Specialized approaches for adult education, professional development, and lifelong learning.',
      image: 'https://readdy.ai/api/search-image?query=Adult%20learning%20book%20cover%20titled%20Adult%20Learning%20Strategies%20with%20professional%20in%20classroom%2C%20continuing%20education%20symbols%2C%20career%20development%20design%2C%20brown%20and%20gold%20colors&width=400&height=300&seq=edu008&orientation=landscape',
      pages: 110,
      difficulty: 'Beginner',
      tags: ['adult learning', 'professional development', 'lifelong learning', 'career']
    },

    // Health Templates (8)
    {
      id: 'health-001',
      title: 'Nutrition and Wellness Guide',
      category: 'Health',
      description: 'Comprehensive approach to healthy eating, nutrition planning, and overall wellness.',
      image: 'https://readdy.ai/api/search-image?query=Nutrition%20book%20cover%20titled%20Nutrition%20and%20Wellness%20Guide%20with%20fresh%20fruits%20and%20vegetables%2C%20healthy%20plate%2C%20wellness%20symbols%2C%20clean%20design%2C%20green%20and%20white%20colors&width=400&height=300&seq=health001&orientation=landscape',
      pages: 140,
      difficulty: 'Beginner',
      tags: ['nutrition', 'wellness', 'healthy eating', 'lifestyle']
    },
    {
      id: 'health-002',
      title: 'Fitness and Exercise Program',
      category: 'Health',
      description: 'Complete fitness guide with workout routines, exercise techniques, and training programs.',
      image: 'https://readdy.ai/api/search-image?query=Fitness%20book%20cover%20titled%20Fitness%20and%20Exercise%20Program%20with%20athletic%20person%20exercising%2C%20dumbbells%2C%20fitness%20equipment%2C%20energetic%20design%2C%20red%20and%20black%20colors&width=400&height=300&seq=health002&orientation=landscape',
      pages: 160,
      difficulty: 'Intermediate',
      tags: ['fitness', 'exercise', 'workout', 'training']
    },
    {
      id: 'health-003',
      title: 'Mental Health and Wellbeing',
      category: 'Health',
      description: 'Strategies for maintaining mental health, managing depression, anxiety, and emotional wellness.',
      image: 'https://readdy.ai/api/search-image?query=Mental%20health%20book%20cover%20titled%20Mental%20Health%20and%20Wellbeing%20with%20peaceful%20mind%20symbols%2C%20brain%20with%20heart%2C%20wellness%20icons%2C%20calming%20design%2C%20soft%20blue%20and%20green%20colors&width=400&height=300&seq=health003&orientation=landscape',
      pages: 150,
      difficulty: 'Intermediate',
      tags: ['mental health', 'wellbeing', 'emotional wellness', 'therapy']
    },
    {
      id: 'health-004',
      title: 'Sleep Optimization Guide',
      category: 'Health',
      description: 'Science-based approaches to improving sleep quality, establishing healthy sleep habits.',
      image: 'https://readdy.ai/api/search-image?query=Sleep%20optimization%20book%20cover%20titled%20Sleep%20Optimization%20Guide%20with%20peaceful%20bedroom%2C%20moon%20and%20stars%2C%20sleep%20symbols%2C%20restful%20design%2C%20dark%20blue%20and%20silver%20colors&width=400&height=300&seq=health004&orientation=landscape',
      pages: 100,
      difficulty: 'Beginner',
      tags: ['sleep', 'rest', 'recovery', 'health']
    },
    {
      id: 'health-005',
      title: 'Weight Management System',
      category: 'Health',
      description: 'Sustainable approach to weight loss, weight maintenance, and healthy body composition.',
      image: 'https://readdy.ai/api/search-image?query=Weight%20management%20book%20cover%20titled%20Weight%20Management%20System%20with%20scale%2C%20healthy%20lifestyle%20symbols%2C%20transformation%20imagery%2C%20motivational%20design%2C%20orange%20and%20blue%20colors&width=400&height=300&seq=health005&orientation=landscape',
      pages: 130,
      difficulty: 'Intermediate',
      tags: ['weight management', 'diet', 'body composition', 'health']
    },
    {
      id: 'health-006',
      title: 'Preventive Healthcare Guide',
      category: 'Health',
      description: 'Proactive approaches to preventing illness, maintaining health, and early disease detection.',
      image: 'https://readdy.ai/api/search-image?query=Preventive%20healthcare%20book%20cover%20titled%20Preventive%20Healthcare%20Guide%20with%20medical%20cross%2C%20health%20checkup%20symbols%2C%20prevention%20icons%2C%20medical%20design%2C%20white%20and%20blue%20colors&width=400&height=300&seq=health006&orientation=landscape',
      pages: 120,
      difficulty: 'Beginner',
      tags: ['preventive care', 'health screening', 'wellness', 'prevention']
    },
    {
      id: 'health-007',
      title: 'Chronic Disease Management',
      category: 'Health',
      description: 'Comprehensive guide to managing chronic conditions and maintaining quality of life.',
      image: 'https://readdy.ai/api/search-image?query=Chronic%20disease%20management%20book%20cover%20titled%20Chronic%20Disease%20Management%20with%20medical%20chart%2C%20care%20symbols%2C%20health%20monitoring%2C%20supportive%20design%2C%20teal%20and%20gray%20colors&width=400&height=300&seq=health007&orientation=landscape',
      pages: 180,
      difficulty: 'Advanced',
      tags: ['chronic disease', 'management', 'treatment', 'healthcare']
    },
    {
      id: 'health-008',
      title: 'Holistic Health Approach',
      category: 'Health',
      description: 'Integrative approach combining traditional and alternative medicine for optimal health.',
      image: 'https://readdy.ai/api/search-image?query=Holistic%20health%20book%20cover%20titled%20Holistic%20Health%20Approach%20with%20yin%20yang%20symbol%2C%20natural%20elements%2C%20wellness%20balance%2C%20integrative%20design%2C%20earth%20tone%20colors&width=400&height=300&seq=health008&orientation=landscape',
      pages: 170,
      difficulty: 'Advanced',
      tags: ['holistic health', 'alternative medicine', 'wellness', 'integrative']
    },

    // Technology Templates (8)
    {
      id: 'tech-001',
      title: 'AI and Machine Learning Guide',
      category: 'Technology',
      description: 'Introduction to artificial intelligence, machine learning concepts, and practical applications.',
      image: 'https://readdy.ai/api/search-image?query=AI%20book%20cover%20titled%20AI%20and%20Machine%20Learning%20Guide%20with%20neural%20network%2C%20robot%20brain%2C%20AI%20symbols%2C%20futuristic%20design%2C%20blue%20and%20silver%20colors&width=400&height=300&seq=tech001&orientation=landscape',
      pages: 200,
      difficulty: 'Advanced',
      tags: ['AI', 'machine learning', 'technology', 'algorithms']
    },
    {
      id: 'tech-002',
      title: 'Cybersecurity Essentials',
      category: 'Technology',
      description: 'Comprehensive guide to cybersecurity, data protection, and digital privacy best practices.',
      image: 'https://readdy.ai/api/search-image?query=Cybersecurity%20book%20cover%20titled%20Cybersecurity%20Essentials%20with%20shield%2C%20lock%20symbols%2C%20security%20icons%2C%20protective%20design%2C%20red%20and%20black%20colors&width=400&height=300&seq=tech002&orientation=landscape',
      pages: 160,
      difficulty: 'Intermediate',
      tags: ['cybersecurity', 'data protection', 'privacy', 'security']
    },
    {
      id: 'tech-003',
      title: 'Web Development Mastery',
      category: 'Technology',
      description: 'Complete guide to modern web development, programming languages, and development frameworks.',
      image: 'https://readdy.ai/api/search-image?query=Web%20development%20book%20cover%20titled%20Web%20Development%20Mastery%20with%20code%20on%20screen%2C%20HTML%20tags%2C%20programming%20symbols%2C%20developer%20design%2C%20green%20and%20black%20colors&width=400&height=300&seq=tech003&orientation=landscape',
      pages: 250,
      difficulty: 'Advanced',
      tags: ['web development', 'programming', 'coding', 'software']
    },
    {
      id: 'tech-004',
      title: 'Cloud Computing Guide',
      category: 'Technology',
      description: 'Understanding cloud services, deployment strategies, and cloud architecture best practices.',
      image: 'https://readdy.ai/api/search-image?query=Cloud%20computing%20book%20cover%20titled%20Cloud%20Computing%20Guide%20with%20cloud%20symbols%2C%20server%20icons%2C%20network%20connections%2C%20tech%20design%2C%20blue%20and%20white%20colors&width=400&height=300&seq=tech004&orientation=landscape',
      pages: 180,
      difficulty: 'Intermediate',
      tags: ['cloud computing', 'infrastructure', 'services', 'architecture']
    },
    {
      id: 'tech-005',
      title: 'Data Science and Analytics',
      category: 'Technology',
      description: 'Comprehensive approach to data analysis, statistical modeling, and business intelligence.',
      image: 'https://readdy.ai/api/search-image?query=Data%20science%20book%20cover%20titled%20Data%20Science%20and%20Analytics%20with%20charts%2C%20graphs%2C%20data%20visualization%2C%20analytics%20symbols%2C%20purple%20and%20orange%20colors&width=400&height=300&seq=tech005&orientation=landscape',
      pages: 220,
      difficulty: 'Advanced',
      tags: ['data science', 'analytics', 'statistics', 'business intelligence']
    },
    {
      id: 'tech-006',
      title: 'Mobile App Development',
      category: 'Technology',
      description: 'Guide to creating mobile applications for iOS and Android platforms using modern frameworks.',
      image: 'https://readdy.ai/api/search-image?query=Mobile%20app%20development%20book%20cover%20titled%20Mobile%20App%20Development%20with%20smartphone%2C%20app%20icons%2C%20mobile%20interface%2C%20development%20design%2C%20bright%20colors&width=400&height=300&seq=tech006&orientation=landscape',
      pages: 190,
      difficulty: 'Advanced',
      tags: ['mobile development', 'apps', 'iOS', 'Android']
    },
    {
      id: 'tech-007',
      title: 'Blockchain and Cryptocurrency',
      category: 'Technology',
      description: 'Understanding blockchain technology, cryptocurrency, and decentralized applications.',
      image: 'https://readdy.ai/api/search-image?query=Blockchain%20book%20cover%20titled%20Blockchain%20and%20Cryptocurrency%20with%20bitcoin%20symbol%2C%20blockchain%20chain%2C%20crypto%20icons%2C%20digital%20design%2C%20gold%20and%20black%20colors&width=400&height=300&seq=tech007&orientation=landscape',
      pages: 170,
      difficulty: 'Advanced',
      tags: ['blockchain', 'cryptocurrency', 'decentralized', 'crypto']
    },
    {
      id: 'tech-008',
      title: 'Internet of Things (IoT)',
      category: 'Technology',
      description: 'Exploring IoT devices, smart home technology, and connected device ecosystems.',
      image: 'https://readdy.ai/api/search-image?query=IoT%20book%20cover%20titled%20Internet%20of%20Things%20with%20connected%20devices%2C%20smart%20home%20icons%2C%20IoT%20network%2C%20technology%20design%2C%20cyan%20and%20gray%20colors&width=400&height=300&seq=tech008&orientation=landscape',
      pages: 150,
      difficulty: 'Intermediate',
      tags: ['IoT', 'smart devices', 'connected', 'automation']
    },

    // Marketing Templates (8)
    {
      id: 'mark-001',
      title: 'Content Marketing Strategy',
      category: 'Marketing',
      description: 'Complete guide to creating, distributing, and measuring effective content marketing campaigns.',
      image: 'https://readdy.ai/api/search-image?query=Content%20marketing%20book%20cover%20titled%20Content%20Marketing%20Strategy%20with%20blog%20icons%2C%20content%20creation%20symbols%2C%20marketing%20funnel%2C%20strategic%20design%2C%20orange%20and%20blue%20colors&width=400&height=300&seq=mark001&orientation=landscape',
      pages: 140,
      difficulty: 'Intermediate',
      tags: ['content marketing', 'strategy', 'content creation', 'campaigns']
    },
    {
      id: 'mark-002',
      title: 'SEO Optimization Mastery',
      category: 'Marketing',
      description: 'Advanced search engine optimization techniques for improving website rankings and organic traffic.',
      image: 'https://readdy.ai/api/search-image?query=SEO%20book%20cover%20titled%20SEO%20Optimization%20Mastery%20with%20search%20magnifying%20glass%2C%20ranking%20arrows%2C%20SEO%20symbols%2C%20optimization%20design%2C%20green%20and%20white%20colors&width=400&height=300&seq=mark002&orientation=landscape',
      pages: 160,
      difficulty: 'Advanced',
      tags: ['SEO', 'search optimization', 'rankings', 'organic traffic']
    },
    {
      id: 'mark-003',
      title: 'Social Media Marketing',
      category: 'Marketing',
      description: 'Comprehensive social media strategy for building brand awareness and engaging audiences.',
      image: 'https://readdy.ai/api/search-image?query=Social%20media%20marketing%20book%20cover%20titled%20Social%20Media%20Marketing%20with%20social%20platform%20icons%2C%20engagement%20symbols%2C%20community%20building%2C%20vibrant%20design%2C%20multiple%20bright%20colors&width=400&height=300&seq=mark003&orientation=landscape',
      pages: 130,
      difficulty: 'Beginner',
      tags: ['social media', 'brand awareness', 'engagement', 'community']
    },
    {
      id: 'mark-004',
      title: 'Email Marketing Automation',
      category: 'Marketing',
      description: 'Building effective email campaigns, automation sequences, and customer nurturing systems.',
      image: 'https://readdy.ai/api/search-image?query=Email%20marketing%20book%20cover%20titled%20Email%20Marketing%20Automation%20with%20email%20envelope%2C%20automation%20gears%2C%20campaign%20symbols%2C%20professional%20design%2C%20blue%20and%20yellow%20colors&width=400&height=300&seq=mark004&orientation=landscape',
      pages: 120,
      difficulty: 'Intermediate',
      tags: ['email marketing', 'automation', 'campaigns', 'nurturing']
    },
    {
      id: 'mark-005',
      title: 'Influencer Marketing Guide',
      category: 'Marketing',
      description: 'Strategies for partnering with influencers and leveraging social proof for brand growth.',
      image: 'https://readdy.ai/api/search-image?query=Influencer%20marketing%20book%20cover%20titled%20Influencer%20Marketing%20Guide%20with%20influencer%20silhouette%2C%20social%20media%20icons%2C%20partnership%20symbols%2C%20trendy%20design%2C%20pink%20and%20purple%20colors&width=400&height=300&seq=mark005&orientation=landscape',
      pages: 110,
      difficulty: 'Intermediate',
      tags: ['influencer marketing', 'partnerships', 'social proof', 'collaboration']
    },
    {
      id: 'mark-006',
      title: 'Paid Advertising Mastery',
      category: 'Marketing',
      description: 'Advanced techniques for Google Ads, Facebook Ads, and other paid advertising platforms.',
      image: 'https://readdy.ai/api/search-image?query=Paid%20advertising%20book%20cover%20titled%20Paid%20Advertising%20Mastery%20with%20ad%20campaign%20icons%2C%20PPC%20symbols%2C%20ROI%20charts%2C%20advertising%20design%2C%20red%20and%20black%20colors&width=400&height=300&seq=mark006&orientation=landscape',
      pages: 180,
      difficulty: 'Advanced',
      tags: ['paid advertising', 'PPC', 'ad campaigns', 'ROI']
    },
    {
      id: 'mark-007',
      title: 'Brand Building Strategy',
      category: 'Marketing',
      description: 'Creating strong brand identity, messaging, and positioning in competitive markets.',
      image: 'https://readdy.ai/api/search-image?query=Brand%20building%20book%20cover%20titled%20Brand%20Building%20Strategy%20with%20brand%20logo%2C%20identity%20symbols%2C%20positioning%20icons%2C%20strategic%20design%2C%20navy%20blue%20and%20gold%20colors&width=400&height=300&seq=mark007&orientation=landscape',
      pages: 150,
      difficulty: 'Intermediate',
      tags: ['brand building', 'identity', 'positioning', 'messaging']
    },
    {
      id: 'mark-008',
      title: 'Marketing Analytics and ROI',
      category: 'Marketing',
      description: 'Measuring marketing performance, analyzing data, and optimizing campaigns for better ROI.',
      image: 'https://readdy.ai/api/search-image?query=Marketing%20analytics%20book%20cover%20titled%20Marketing%20Analytics%20and%20ROI%20with%20dashboard%20charts%2C%20ROI%20graphs%2C%20analytics%20symbols%2C%20data%20design%2C%20purple%20and%20green%20colors&width=400&height=300&seq=mark008&orientation=landscape',
      pages: 170,
      difficulty: 'Advanced',
      tags: ['marketing analytics', 'ROI', 'performance', 'measurement']
    },

    // Finance Templates (8)
    {
      id: 'fin-001',
      title: 'Investment Strategy Guide',
      category: 'Finance',
      description: 'Comprehensive approach to building investment portfolios and long-term wealth creation.',
      image: 'https://readdy.ai/api/search-image?query=Investment%20book%20cover%20titled%20Investment%20Strategy%20Guide%20with%20stock%20charts%2C%20portfolio%20symbols%2C%20wealth%20building%20icons%2C%20financial%20design%2C%20dark%20blue%20and%20gold%20colors&width=400&height=300&seq=fin001&orientation=landscape',
      pages: 180,
      difficulty: 'Advanced',
      tags: ['investment', 'portfolio', 'wealth building', 'strategy']
    },
    {
      id: 'fin-002',
      title: 'Personal Budgeting Mastery',
      category: 'Finance',
      description: 'Effective budgeting techniques, expense tracking, and financial goal achievement.',
      image: 'https://readdy.ai/api/search-image?query=Personal%20budgeting%20book%20cover%20titled%20Personal%20Budgeting%20Mastery%20with%20calculator%2C%20budget%20spreadsheet%2C%20money%20symbols%2C%20financial%20planning%20design%2C%20green%20and%20white%20colors&width=400&height=300&seq=fin002&orientation=landscape',
      pages: 120,
      difficulty: 'Beginner',
      tags: ['budgeting', 'expense tracking', 'financial planning', 'money management']
    },
    {
      id: 'fin-003',
      title: 'Real Estate Investment',
      category: 'Finance',
      description: 'Guide to real estate investing, property analysis, and rental income strategies.',
      image: 'https://readdy.ai/api/search-image?query=Real%20estate%20investment%20book%20cover%20titled%20Real%20Estate%20Investment%20with%20house%2C%20property%20keys%2C%20investment%20charts%2C%20real%20estate%20design%2C%20brown%20and%20gold%20colors&width=400&height=300&seq=fin003&orientation=landscape',
      pages: 200,
      difficulty: 'Advanced',
      tags: ['real estate', 'property investment', 'rental income', 'analysis']
    },
    {
      id: 'fin-004',
      title: 'Retirement Planning Guide',
      category: 'Finance',
      description: 'Comprehensive retirement planning strategies, 401k optimization, and pension management.',
      image: 'https://readdy.ai/api/search-image?query=Retirement%20planning%20book%20cover%20titled%20Retirement%20Planning%20Guide%20with%20retirement%20symbols%2C%20nest%20egg%2C%20financial%20security%20icons%2C%20planning%20design%2C%20navy%20blue%20and%20silver%20colors&width=400&height=300&seq=fin004&orientation=landscape',
      pages: 160,
      difficulty: 'Intermediate',
      tags: ['retirement planning', 'pension', '401k', 'financial security']
    },
    {
      id: 'fin-005',
      title: 'Debt Management System',
      category: 'Finance',
      description: 'Strategies for paying off debt, improving credit scores, and achieving financial freedom.',
      image: 'https://readdy.ai/api/search-image?query=Debt%20management%20book%20cover%20titled%20Debt%20Management%20System%20with%20credit%20cards%2C%20debt%20payoff%20chart%2C%20financial%20freedom%20symbols%2C%20relief%20design%2C%20red%20and%20green%20colors&width=400&height=300&seq=fin005&orientation=landscape',
      pages: 140,
      difficulty: 'Intermediate',
      tags: ['debt management', 'credit score', 'financial freedom', 'payoff strategy']
    },
    {
      id: 'fin-006',
      title: 'Tax Optimization Strategies',
      category: 'Finance',
      description: 'Legal tax reduction techniques, deductions, and tax-efficient investment strategies.',
      image: 'https://readdy.ai/api/search-image?query=Tax%20optimization%20book%20cover%20titled%20Tax%20Optimization%20Strategies%20with%20tax%20forms%2C%20calculator%2C%20savings%20symbols%2C%20tax%20planning%20design%2C%20gray%20and%20blue%20colors&width=400&height=300&seq=fin006&orientation=landscape',
      pages: 150,
      difficulty: 'Advanced',
      tags: ['tax optimization', 'tax planning', 'deductions', 'tax savings']
    },
    {
      id: 'fin-007',
      title: 'Emergency Fund Building',
      category: 'Finance',
      description: 'Creating financial safety nets, emergency preparedness, and risk management strategies.',
      image: 'https://readdy.ai/api/search-image?query=Emergency%20fund%20book%20cover%20titled%20Emergency%20Fund%20Building%20with%20piggy%20bank%2C%20safety%20net%2C%20emergency%20symbols%2C%20security%20design%2C%20orange%20and%20blue%20colors&width=400&height=300&seq=fin007&orientation=landscape',
      pages: 100,
      difficulty: 'Beginner',
      tags: ['emergency fund', 'financial safety', 'risk management', 'savings']
    },
    {
      id: 'fin-008',
      title: 'Cryptocurrency Investment',
      category: 'Finance',
      description: 'Understanding cryptocurrency markets, trading strategies, and digital asset management.',
      image: 'https://readdy.ai/api/search-image?query=Cryptocurrency%20investment%20book%20cover%20titled%20Cryptocurrency%20Investment%20with%20bitcoin%2C%20crypto%20charts%2C%20digital%20wallet%2C%20crypto%20design%2C%20gold%20and%20black%20colors&width=400&height=300&seq=fin008&orientation=landscape',
      pages: 170,
      difficulty: 'Advanced',
      tags: ['cryptocurrency', 'crypto trading', 'digital assets', 'blockchain']
    },

    // Lifestyle Templates (8)
    {
      id: 'life-001',
      title: 'Minimalism and Decluttering',
      category: 'Lifestyle',
      description: 'Guide to minimalist living, decluttering your space, and simplifying your life.',
      image: 'https://readdy.ai/api/search-image?query=Minimalism%20book%20cover%20titled%20Minimalism%20and%20Decluttering%20with%20clean%20simple%20room%2C%20minimal%20design%2C%20declutter%20symbols%2C%20zen%20aesthetic%2C%20white%20and%20gray%20colors&width=400&height=300&seq=life001&orientation=landscape',
      pages: 120,
      difficulty: 'Beginner',
      tags: ['minimalism', 'decluttering', 'simple living', 'organization']
    },
    {
      id: 'life-002',
      title: 'Home Organization System',
      category: 'Lifestyle',
      description: 'Comprehensive home organization strategies, storage solutions, and maintenance systems.',
      image: 'https://readdy.ai/api/search-image?query=Home%20organization%20book%20cover%20titled%20Home%20Organization%20System%20with%20organized%20closet%2C%20storage%20boxes%2C%20organization%20tools%2C%20systematic%20design%2C%20blue%20and%20white%20colors&width=400&height=300&seq=life002&orientation=landscape',
      pages: 140,
      difficulty: 'Intermediate',
      tags: ['home organization', 'storage', 'organization systems', 'tidiness']
    },
    {
      id: 'life-003',
      title: 'Work-Life Balance Guide',
      category: 'Lifestyle',
      description: 'Achieving healthy work-life balance, setting boundaries, and prioritizing well-being.',
      image: 'https://readdy.ai/api/search-image?query=Work-life%20balance%20book%20cover%20titled%20Work-Life%20Balance%20Guide%20with%20scale%20balance%2C%20work%20and%20life%20symbols%2C%20harmony%20icons%2C%20balanced%20design%2C%20green%20and%20purple%20colors&width=400&height=300&seq=life003&orientation=landscape',
      pages: 130,
      difficulty: 'Intermediate',
      tags: ['work-life balance', 'boundaries', 'well-being', 'lifestyle']
    },
    {
      id: 'life-004',
      title: 'Sustainable Living Practices',
      category: 'Lifestyle',
      description: 'Eco-friendly lifestyle choices, sustainable practices, and environmental consciousness.',
      image: 'https://readdy.ai/api/search-image?query=Sustainable%20living%20book%20cover%20titled%20Sustainable%20Living%20Practices%20with%20earth%2C%20green%20leaves%2C%20eco%20symbols%2C%20environmental%20design%2C%20green%20and%20brown%20colors&width=400&height=300&seq=life004&orientation=landscape',
      pages: 150,
      difficulty: 'Intermediate',
      tags: ['sustainable living', 'eco-friendly', 'environmental', 'green lifestyle']
    },
    {
      id: 'life-005',
      title: 'Digital Detox Guide',
      category: 'Lifestyle',
      description: 'Reducing screen time, managing digital overwhelm, and reconnecting with offline life.',
      image: 'https://readdy.ai/api/search-image?query=Digital%20detox%20book%20cover%20titled%20Digital%20Detox%20Guide%20with%20phone%20with%20X%20mark%2C%20nature%20scene%2C%20offline%20symbols%2C%20peaceful%20design%2C%20soft%20blue%20and%20green%20colors&width=400&height=300&seq=life005&orientation=landscape',
      pages: 110,
      difficulty: 'Beginner',
      tags: ['digital detox', 'screen time', 'offline living', 'mindfulness']
    },
    {
      id: 'life-006',
      title: 'Time Management Mastery',
      category: 'Lifestyle',
      description: 'Advanced time management techniques, priority setting, and productivity optimization.',
      image: 'https://readdy.ai/api/search-image?query=Time%20management%20book%20cover%20titled%20Time%20Management%20Mastery%20with%20clock%2C%20calendar%2C%20productivity%20symbols%2C%20efficiency%20design%2C%20orange%20and%20black%20colors&width=400&height=300&seq=life006&orientation=landscape',
      pages: 160,
      difficulty: 'Advanced',
      tags: ['time management', 'productivity', 'priority setting', 'efficiency']
    },
    {
      id: 'life-007',
      title: 'Relationship Building Guide',
      category: 'Lifestyle',
      description: 'Building meaningful relationships, communication skills, and social connection strategies.',
      image: 'https://readdy.ai/api/search-image?query=Relationship%20building%20book%20cover%20titled%20Relationship%20Building%20Guide%20with%20connected%20people%2C%20heart%20symbols%2C%20communication%20icons%2C%20social%20design%2C%20warm%20colors&width=400&height=300&seq=life007&orientation=landscape',
      pages: 140,
      difficulty: 'Intermediate',
      tags: ['relationships', 'communication', 'social connection', 'friendship']
    },
    {
      id: 'life-008',
      title: 'Creative Hobby Development',
      category: 'Lifestyle',
      description: 'Exploring creative pursuits, developing artistic skills, and finding creative fulfillment.',
      image: 'https://readdy.ai/api/search-image?query=Creative%20hobbies%20book%20cover%20titled%20Creative%20Hobby%20Development%20with%20art%20palette%2C%20creative%20tools%2C%20artistic%20symbols%2C%20inspiring%20design%2C%20rainbow%20colors&width=400&height=300&seq=life008&orientation=landscape',
      pages: 120,
      difficulty: 'Beginner',
      tags: ['creative hobbies', 'artistic skills', 'creativity', 'fulfillment']
    },

    // Travel Templates (4)
    {
      id: 'travel-001',
      title: 'Solo Travel Adventure Guide',
      category: 'Travel',
      description: 'Complete guide to safe and enjoyable solo travel experiences around the world.',
      image: 'https://readdy.ai/api/search-image?query=Solo%20travel%20book%20cover%20titled%20Solo%20Travel%20Adventure%20Guide%20with%20backpacker%20silhouette%2C%20world%20map%2C%20adventure%20symbols%2C%20travel%20design%2C%20blue%20and%20orange%20colors&width=400&height=300&seq=travel001&orientation=landscape',
      pages: 160,
      difficulty: 'Intermediate',
      tags: ['solo travel', 'adventure', 'travel safety', 'exploration']
    },
    {
      id: 'travel-002',
      title: 'Budget Travel Mastery',
      category: 'Travel',
      description: 'Strategies for traveling the world on a budget without compromising on experiences.',
      image: 'https://readdy.ai/api/search-image?query=Budget%20travel%20book%20cover%20titled%20Budget%20Travel%20Mastery%20with%20piggy%20bank%2C%20passport%2C%20budget%20symbols%2C%20affordable%20travel%20design%2C%20green%20and%20yellow%20colors&width=400&height=300&seq=travel002&orientation=landscape',
      pages: 140,
      difficulty: 'Beginner',
      tags: ['budget travel', 'affordable travel', 'travel savings', 'cost-effective']
    },
    {
      id: 'travel-003',
      title: 'Cultural Immersion Travel',
      category: 'Travel',
      description: 'Deep cultural experiences, local interactions, and authentic travel encounters.',
      image: 'https://readdy.ai/api/search-image?query=Cultural%20immersion%20travel%20book%20cover%20titled%20Cultural%20Immersion%20Travel%20with%20cultural%20symbols%2C%20local%20people%2C%20authentic%20experiences%2C%20cultural%20design%2C%20earth%20tone%20colors&width=400&height=300&seq=travel003&orientation=landscape',
      pages: 180,
      difficulty: 'Advanced',
      tags: ['cultural immersion', 'authentic travel', 'local culture', 'cultural exchange']
    },
    {
      id: 'travel-004',
      title: 'Family Travel Planning',
      category: 'Travel',
      description: 'Comprehensive guide to planning memorable family vacations and kid-friendly adventures.',
      image: 'https://readdy.ai/api/search-image?query=Family%20travel%20book%20cover%20titled%20Family%20Travel%20Planning%20with%20family%20silhouette%2C%20suitcases%2C%20family%20vacation%20symbols%2C%20fun%20design%2C%20bright%20cheerful%20colors&width=400&height=300&seq=travel004&orientation=landscape',
      pages: 150,
      difficulty: 'Intermediate',
      tags: ['family travel', 'family vacation', 'kid-friendly', 'travel planning']
    },

    // Cooking Templates (4)
    {
      id: 'cook-001',
      title: 'Meal Planning and Prep',
      category: 'Cooking',
      description: 'Efficient meal planning strategies, batch cooking, and weekly meal preparation systems.',
      image: 'https://readdy.ai/api/search-image?query=Meal%20planning%20cookbook%20cover%20titled%20Meal%20Planning%20and%20Prep%20with%20meal%20prep%20containers%2C%20organized%20kitchen%2C%20cooking%20schedule%2C%20systematic%20design%2C%20green%20and%20white%20colors&width=400&height=300&seq=cook001&orientation=landscape',
      pages: 120,
      difficulty: 'Beginner',
      tags: ['meal planning', 'meal prep', 'cooking efficiency', 'kitchen organization']
    },
    {
      id: 'cook-002',
      title: 'International Cuisine Mastery',
      category: 'Cooking',
      description: 'Exploring world cuisines, authentic recipes, and international cooking techniques.',
      image: 'https://readdy.ai/api/search-image?query=International%20cuisine%20cookbook%20cover%20titled%20International%20Cuisine%20Mastery%20with%20world%20map%2C%20diverse%20dishes%2C%20cultural%20food%20symbols%2C%20culinary%20design%2C%20vibrant%20colors&width=400&height=300&seq=cook002&orientation=landscape',
      pages: 200,
      difficulty: 'Advanced',
      tags: ['international cuisine', 'world food', 'cooking techniques', 'recipes']
    },
    {
      id: 'cook-003',
      title: 'Baking and Pastry Guide',
      category: 'Cooking',
      description: 'Comprehensive baking techniques, pastry skills, and dessert creation mastery.',
      image: 'https://readdy.ai/api/search-image?query=Baking%20cookbook%20cover%20titled%20Baking%20and%20Pastry%20Guide%20with%20fresh%20bread%2C%20pastries%2C%20baking%20tools%2C%20bakery%20design%2C%20warm%20brown%20and%20cream%20colors&width=400&height=300&seq=cook003&orientation=landscape',
      pages: 180,
      difficulty: 'Advanced',
      tags: ['baking', 'pastry', 'desserts', 'baking techniques']
    },
    {
      id: 'cook-004',
      title: 'Healthy Cooking Essentials',
      category: 'Cooking',
      description: 'Nutritious cooking methods, healthy recipes, and wellness-focused meal preparation.',
      image: 'https://readdy.ai/api/search-image?query=Healthy%20cooking%20cookbook%20cover%20titled%20Healthy%20Cooking%20Essentials%20with%20fresh%20vegetables%2C%20healthy%20ingredients%2C%20nutrition%20symbols%2C%20wellness%20design%2C%20green%20and%20orange%20colors&width=400&height=300&seq=cook004&orientation=landscape',
      pages: 140,
      difficulty: 'Intermediate',
      tags: ['healthy cooking', 'nutritious meals', 'wellness', 'clean eating']
    },

    // Biography Templates (4)
    {
      id: 'bio-001',
      title: 'Personal Memoir Writing',
      category: 'Biography',
      description: 'Guide to writing compelling personal memoirs and life story narratives.',
      image: 'https://readdy.ai/api/search-image?query=Personal%20memoir%20book%20cover%20titled%20Personal%20Memoir%20Writing%20with%20vintage%20photo%20frame%2C%20handwritten%20text%2C%20memory%20symbols%2C%20nostalgic%20design%2C%20sepia%20and%20gold%20colors&width=400&height=300&seq=bio001&orientation=landscape',
      pages: 160,
      difficulty: 'Intermediate',
      tags: ['memoir', 'autobiography', 'life story', 'personal narrative']
    },
    {
      id: 'bio-002',
      title: 'Family History Documentation',
      category: 'Biography',
      description: 'Researching and documenting family genealogy, heritage, and ancestral stories.',
      image: 'https://readdy.ai/api/search-image?query=Family%20history%20book%20cover%20titled%20Family%20History%20Documentation%20with%20family%20tree%2C%20genealogy%20chart%2C%20heritage%20symbols%2C%20historical%20design%2C%20brown%20and%20green%20colors&width=400&height=300&seq=bio002&orientation=landscape',
      pages: 140,
      difficulty: 'Advanced',
      tags: ['family history', 'genealogy', 'heritage', 'ancestry']
    },
    {
      id: 'bio-003',
      title: 'Professional Biography',
      category: 'Biography',
      description: 'Creating compelling professional biographies for career advancement and personal branding.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20biography%20book%20cover%20titled%20Professional%20Biography%20with%20business%20portrait%2C%20career%20timeline%2C%20achievement%20symbols%2C%20professional%20design%2C%20navy%20blue%20and%20silver%20colors&width=400&height=300&seq=bio003&orientation=landscape',
      pages: 100,
      difficulty: 'Beginner',
      tags: ['professional biography', 'career', 'personal branding', 'achievements']
    },
    {
      id: 'bio-004',
      title: 'Celebrity Biography Template',
      category: 'Biography',
      description: 'Framework for writing engaging celebrity biographies and public figure profiles.',
      image: 'https://readdy.ai/api/search-image?query=Celebrity%20biography%20book%20cover%20titled%20Celebrity%20Biography%20Template%20with%20spotlight%2C%20fame%20symbols%2C%20star%20icons%2C%20glamorous%20design%2C%20gold%20and%20black%20colors&width=400&height=300&seq=bio004&orientation=landscape',
      pages: 220,
      difficulty: 'Advanced',
      tags: ['celebrity biography', 'public figure', 'biographical writing', 'profile']
    }
  ];

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExportBook = () => {
    // Create a simple export notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3';
    notification.innerHTML = `
      <i className="ri-check-line text-xl"></i>
      <span className="font-medium">Book exported successfully!</span>
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
    setPreviewTemplate(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-book-open-line text-3xl text-blue-600"></i>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your eBook Template</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Select from over 100 professionally designed templates across 13 categories. 
          Each template includes structure, content guidelines, and industry-specific insights.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        <div className="mt-4 text-center text-gray-600">
          Showing {filteredTemplates.length} templates
          {selectedCategory !== 'All' && ` in ${selectedCategory}`}
          {searchTerm && ` matching "${searchTerm}"`}
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow group"
          >
            <div className="relative cursor-pointer" onClick={() => onSelectTemplate(template)}>
              <img
                src={template.image}
                alt={template.title}
                className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute top-3 right-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(template.difficulty)}`}>
                  {template.difficulty}
                </span>
              </div>
            </div>

            <div className="p-5">
              <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">
                {template.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {template.description}
              </p>

              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <i className="ri-pages-line"></i>
                  <span>{template.estimatedPages} pages</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  <span>{template.estimatedTime}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => onSelectTemplate(template)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  Use Template
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewTemplate(template);
                  }}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700"
                  title="Preview Book"
                >
                  <i className="ri-eye-line"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Book Preview Modal */}
      {previewTemplate && (
        <BookPreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onExport={handleExportBook}
        />
      )}
    </div>
  );
};

export default TemplateSelector;